// Product images — the one place that knows where they are stored and how
// they are shown.
//
// Stored in the self-hosted Supabase storage on the VPS, in the PRIVATE
// `product-images` bucket (migration 0040): only active staff can read the
// objects, and nobody can open one through a public link. Images are for the
// people using the POS, never for customers.
//
// products.image_url therefore holds the object PATH ("products/<id>/….webp"),
// and the app turns it into a signed URL valid for an hour when it is shown.
// Signed URLs are made in one request per screen (see signedUrlFor) and cached
// until shortly before they expire. A plain http(s) URL typed into a product
// (e.g. through the product import) is shown as it is.
//
// Photos from a phone are 3–8 MB and 4000px wide; the bucket takes 2 MB, and a
// POS tile is 160px. So every image is shrunk in the browser first — longest
// side 1200px, WebP (JPEG where the browser cannot encode WebP) — which also
// keeps the POS grid light on a booth's tethered connection.

import { supabase } from '@/lib/supabase';

const BUCKET = 'product-images';
const MAX_SIDE = 1200;
const MAX_BYTES = 2 * 1024 * 1024;
/** How long a signed URL lives, and how early it is renewed. */
const SIGNED_SECONDS = 60 * 60;
const RENEW_BEFORE_MS = 10 * 60 * 1000;
/** Public URLs saved before the bucket went private (0040 converts them). */
const PUBLIC_MARKER = `/storage/v1/object/public/${BUCKET}/`;

export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/*';
const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif)$/i;

export const isImageFile = (f: File) => f.type.startsWith('image/') || IMAGE_EXT.test(f.name);

async function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** Shrink and re-encode an image in the browser. Throws a readable message for formats the browser cannot open. */
export async function compressImage(file: File): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(
      `"${file.name}" tidak bisa dibaca browser. Pakai JPG, PNG, atau WebP (foto HEIC dari iPhone: ubah dulu ke JPG).`,
    );
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Browser tidak bisa memproses gambar.');
  // White behind transparent PNGs, so a JPEG fallback does not turn them black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  for (const quality of [0.85, 0.72, 0.6, 0.45]) {
    let blob = await encode(canvas, 'image/webp', quality);
    // Browsers that cannot encode WebP silently hand back PNG.
    if (!blob || blob.type !== 'image/webp') blob = await encode(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= MAX_BYTES) return blob;
  }
  throw new Error(`"${file.name}" masih lebih dari 2 MB setelah dikecilkan.`);
}

/**
 * Compress and store a product image; returns the object path to save in
 * products.image_url. `key` groups the files (the product id, or the SKU
 * before a product exists); every upload gets a fresh name.
 */
export async function uploadProductImage(file: File, key: string): Promise<string> {
  const blob = await compressImage(file);
  const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
  const safeKey = key.replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 80) || 'product';
  const path = `products/${safeKey}/${Date.now()}-${crypto.randomUUID().slice(0, 8)}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: blob.type,
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw new Error(`Upload gagal: ${error.message}`);
  return path;
}

/** The object path in our bucket for a stored value, or null for an external URL / nothing. */
export function storagePathOf(value: string | null | undefined): string | null {
  if (!value) return null;
  const i = value.indexOf(PUBLIC_MARKER);
  if (i !== -1) return decodeURIComponent(value.slice(i + PUBLIC_MARKER.length).split('?')[0]);
  if (/^https?:\/\//i.test(value)) return null;
  return value.replace(/^\/+/, '');
}

/** Best-effort removal of an image this app stored. External URLs are left alone. */
export async function removeStoredImage(value: string | null | undefined): Promise<void> {
  const path = storagePathOf(value);
  if (!path) return;
  await supabase.storage.from(BUCKET).remove([path]).catch(() => undefined);
}

// ── Showing images: batched, cached signed URLs ─────────────────────────────

const signedCache = new Map<string, { url: string; expiresAt: number }>();
let pending: Map<string, ((url: string | null) => void)[]> | null = null;

async function flush(batch: Map<string, ((url: string | null) => void)[]>) {
  const paths = [...batch.keys()];
  const byPath = new Map<string, string>();
  try {
    const { data } = await supabase.storage.from(BUCKET).createSignedUrls(paths, SIGNED_SECONDS);
    for (const item of data ?? []) {
      if (item.path && item.signedUrl) byPath.set(item.path, item.signedUrl);
    }
  } catch {
    /* every waiter gets null below; the thumbnail shows its placeholder */
  }
  const expiresAt = Date.now() + SIGNED_SECONDS * 1000;
  for (const [path, waiters] of batch) {
    const url = byPath.get(path) ?? null;
    if (url) signedCache.set(path, { url, expiresAt });
    waiters.forEach((w) => w(url));
  }
}

/**
 * The URL to put in <img src> for a stored value. Our objects get a signed
 * URL — every call made in the same tick shares one request, so a grid of 80
 * products costs one round trip, not 80. External URLs come back unchanged.
 */
export function signedUrlFor(value: string | null | undefined): Promise<string | null> {
  if (!value) return Promise.resolve(null);
  const path = storagePathOf(value);
  if (!path) return Promise.resolve(value); // external http(s) URL
  const hit = signedCache.get(path);
  if (hit && hit.expiresAt - Date.now() > RENEW_BEFORE_MS) return Promise.resolve(hit.url);

  return new Promise((resolve) => {
    if (!pending) {
      pending = new Map();
      const batch = pending;
      setTimeout(() => {
        pending = null;
        void flush(batch);
      }, 0);
    }
    pending.set(path, [...(pending.get(path) ?? []), resolve]);
  });
}

/** Forget a cached signed URL (e.g. the image failed to load because it expired). */
export function forgetSignedUrl(value: string | null | undefined) {
  const path = storagePathOf(value);
  if (path) signedCache.delete(path);
}

// ── Matching files to products ───────────────────────────────────────────────

/**
 * SKU comparison key: letters and digits only, lower case. File systems and
 * people mangle SKUs in file names — "OPH-L#HPAFCLEAR-18.00" becomes
 * "OPH-L_HPAFCLEAR-18.00.jpg" or "oph-l hpafclear 18.00.JPG" — and all of
 * those reduce to the same key.
 */
export const skuKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');

/** A file name without its image extension. "A-18.00.jpg" → "A-18.00" (the ".00" is not an extension). */
export const baseName = (fileName: string) => fileName.replace(IMAGE_EXT, '');
